import { defineStore } from 'pinia';
import { ProfileState } from '@/types/store';
import { ExtendedOrganizationItem } from '@/schemas/organizationSchema';
import { ExtendedRelatedNameItem } from '@/schemas/relatedNameSchema';
import { firestore } from '@/firebase/firestore';
import { collection, onSnapshot, query, where, orderBy, CollectionReference, Query, WhereFilterOp, getDocs } from '@/firebase/firestore';
import { useUserStore } from './user.store';
import orderByLodash from 'lodash/orderBy';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import { watch } from 'vue';

// Define interface for tracking deltas
export interface Delta<T> {
    added: T[];
    updated: T[];
    deleted: T[];
    unchanged: T[];
}

const defaultState: ProfileState = {
    profile: null,
    updatedProfile: null,
    organizations: [],
    updatedOrganizations: [],
    relatedNames: [],
    updatedRelatedNames: [],
    socialLinks: [],
    updatedSocialLinks: [],
    watchersInitialized: false,
    boundCollections: new Set<string>(),
    profileHeaderParams: {
        updatedAt: null,
        ready: true,
        includeBase: false,
        showAll: false,
        showDate: true,
        showProjectSelect: true
    },
    selectedProjects: null,
    profileInitialized: false,
    organizationsMigrated: false,
    socialLinksMigrated: false,
    isReadyForEditing: false,
};

// Utility types
type ArrayKeys<T> = {
    [K in keyof T]: T[K] extends Array<any> ? K : never;
}[keyof T];

type ArrayElement<T> = T extends Array<infer U> ? U : never;

// Fields to ignore when comparing objects (Firestore metadata fields)
export const METADATA_FIELDS = ['createdAt', 'updatedAt', 'createdBy', 'updatedBy'];

/**
 * Enhanced deduplication function that ensures we don't have duplicates in our arrays
 * This is a more thorough check that ignores Firestore metadata fields
 * 
 * @param array The array to deduplicate
 * @param keyField The field to use as the unique key (default: 'id')
 * @param label Log label for debugging
 * @returns Deduplicated array
 */
function thoroughlyDeduplicateArray<T>(
    array: T[],
    keyField: string = 'id',
    label: string = 'array'
): T[] {
    if (!Array.isArray(array)) {
        console.log(`${label} is not an array, returning empty array`);
        return [];
    }

    // First filter out any items without the key field
    const validItems = array.filter(item => item && typeof item === 'object' && keyField in item);

    // Use Map for O(1) lookup to deduplicate
    const uniqueMap = new Map<string, T>();
    let duplicateCount = 0;

    validItems.forEach(item => {
        const key = (item as any)[keyField];
        if (uniqueMap.has(key)) {
            duplicateCount++;

            // If we have duplicates, take the one with the most fields populated
            // ignoring metadata fields
            const existingItem = uniqueMap.get(key)!;

            // Count meaningful fields (exclude metadata fields)
            const existingKeysCount = Object.keys(existingItem as any)
                .filter(k => !METADATA_FIELDS.includes(k))
                .filter(k =>
                    (existingItem as any)[k] !== null &&
                    (existingItem as any)[k] !== undefined &&
                    (existingItem as any)[k] !== ''
                ).length;

            const newKeysCount = Object.keys(item as any)
                .filter(k => !METADATA_FIELDS.includes(k))
                .filter(k =>
                    (item as any)[k] !== null &&
                    (item as any)[k] !== undefined &&
                    (item as any)[k] !== ''
                ).length;

            // If the new item has more populated fields (excluding metadata), use it instead
            if (newKeysCount > existingKeysCount) {
                console.log(`Replacing item with id ${key} because it has more populated fields`);
                uniqueMap.set(key, item);
            } else {
                // Prefer items with metadata over those without
                const existingHasMetadata = METADATA_FIELDS.some(field => field in (existingItem as any));
                const newHasMetadata = METADATA_FIELDS.some(field => field in (item as any));

                if (newHasMetadata && !existingHasMetadata) {
                    console.log(`Replacing item with id ${key} because it has metadata and existing item doesn't`);
                    uniqueMap.set(key, item);
                }
            }
        } else {
            uniqueMap.set(key, item);
        }
    });

    const result = Array.from(uniqueMap.values());

    if (duplicateCount > 0) {
        console.log(`Removed ${duplicateCount} duplicate(s) from ${label}`);
    }

    return result;
}

/**
 * Computes delta between original and updated arrays
 * This function identifies which items were added, updated, deleted, or unchanged
 * 
 * @param originalArray The original array of items
 * @param updatedArray The updated array of items
 * @param keyField The field to use as the unique key (default: 'id')
 * @returns An object with arrays of added, updated, deleted, and unchanged items
 */
export function computeArrayDelta<T extends { [key: string]: any }>(
    originalArray: T[],
    updatedArray: T[],
    keyField: string = 'id'
): Delta<T> {
    // Initialize result object
    const result: Delta<T> = {
        added: [],
        updated: [],
        deleted: [],
        unchanged: []
    };

    // Handle edge cases
    if (!Array.isArray(originalArray) && !Array.isArray(updatedArray)) {
        return result;
    }

    // Ensure arrays exist
    const original = Array.isArray(originalArray) ? originalArray : [];
    const updated = Array.isArray(updatedArray) ? updatedArray : [];

    // Create maps for more efficient lookups
    const originalMap = new Map<string, T>();
    const updatedMap = new Map<string, T>();

    // Populate the original items map
    original.forEach(item => {
        if (item && item[keyField]) {
            originalMap.set(String(item[keyField]), item);
        }
    });

    // Populate the updated items map and identify added/updated items
    updated.forEach(item => {
        if (!item || !item[keyField]) {
            return;
        }

        const key = String(item[keyField]);
        updatedMap.set(key, item);

        if (!originalMap.has(key)) {
            // Item is in updated but not original - it was added
            result.added.push(item);
        } else {
            // Item exists in both arrays - check if it was updated
            const originalItem = originalMap.get(key)!;
            
            // Create clean copies without metadata fields for comparison
            const cleanOriginal = { ...originalItem };
            const cleanUpdated = { ...item };
            
            // Remove metadata fields for comparison
            METADATA_FIELDS.forEach(field => {
                delete cleanOriginal[field];
                delete cleanUpdated[field];
            });

            // If objects are not equal, mark as updated
            if (!isEqual(cleanOriginal, cleanUpdated)) {
                result.updated.push(item);
            } else {
                result.unchanged.push(originalItem);
            }
        }
    });

    // Identify deleted items (in original but not in updated)
    original.forEach(item => {
        if (item && item[keyField] && !updatedMap.has(String(item[keyField]))) {
            result.deleted.push(item);
        }
    });

    return result;
}

/**
 * Computes delta between original and updated objects
 * This function identifies which fields were changed
 * 
 * @param originalObj The original object
 * @param updatedObj The updated object
 * @param ignoreFields Optional array of fields to ignore in the comparison
 * @returns An object with the changed fields and their values
 */
export function computeObjectDelta<T extends { [key: string]: any }>(
    originalObj: T | null,
    updatedObj: T | null,
    ignoreFields: string[] = METADATA_FIELDS
): { [key: string]: { original: any, updated: any } } {
    const changes: { [key: string]: { original: any, updated: any } } = {};
    
    // Handle null/undefined cases
    if (!originalObj && !updatedObj) {
        return changes;
    }
    
    const original = originalObj || {} as T;
    const updated = updatedObj || {} as T;
    
    // Get all keys from both objects
    const allKeys = new Set<string>(
        [...Object.keys(original), ...Object.keys(updated)]
            .filter(key => !ignoreFields.includes(key))
    );
    
    // Check each field for changes
    allKeys.forEach(key => {
        const originalValue = original[key];
        const updatedValue = updated[key];
        
        // Handle arrays specially (recursively compute delta)
        if (Array.isArray(originalValue) && Array.isArray(updatedValue)) {
            // For arrays of objects with IDs, use computeArrayDelta
            if (originalValue.length > 0 && typeof originalValue[0] === 'object' && 'id' in originalValue[0]) {
                const arrayDelta = computeArrayDelta(originalValue, updatedValue);
                if (arrayDelta.added.length > 0 || arrayDelta.updated.length > 0 || arrayDelta.deleted.length > 0) {
                    changes[key] = { 
                        original: originalValue,
                        updated: updatedValue
                    };
                }
            } 
            // For primitive arrays or objects without IDs, use direct comparison
            else if (!isEqual(originalValue, updatedValue)) {
                changes[key] = { 
                    original: originalValue,
                    updated: updatedValue
                };
            }
        } 
        // Handle nested objects (recursively compute delta)
        else if (
            originalValue && updatedValue && 
            typeof originalValue === 'object' && typeof updatedValue === 'object' &&
            !Array.isArray(originalValue) && !Array.isArray(updatedValue)
        ) {
            const nestedChanges = computeObjectDelta(originalValue, updatedValue, ignoreFields);
            if (Object.keys(nestedChanges).length > 0) {
                changes[key] = { 
                    original: originalValue,
                    updated: updatedValue
                };
            }
        } 
        // Handle primitive values with direct comparison
        else if (!isEqual(originalValue, updatedValue)) {
            changes[key] = { 
                original: originalValue,
                updated: updatedValue
            };
        }
    });
    
    return changes;
}

export const useProfileStore = defineStore('profile', {
    state: (): ProfileState => ({ ...defaultState }),
    getters: {
        getState: (state) => <K extends keyof ProfileState>(key: K): ProfileState[K] => {
            return state[key];
        },
        
        // New getters to compute deltas
        getProfileDelta: (state) => () => {
            return computeObjectDelta(state.profile, state.updatedProfile);
        },
        
        getOrganizationsDelta: (state) => () => {
            return computeArrayDelta(state.organizations, state.updatedOrganizations);
        },
        
        getRelatedNamesDelta: (state) => () => {
            return computeArrayDelta(state.relatedNames, state.updatedRelatedNames);
        },
        
        // Get all deltas combined in a single object
        getAllDeltas: (state) => () => {
            return {
                profile: computeObjectDelta(state.profile, state.updatedProfile),
                organizations: computeArrayDelta(state.organizations, state.updatedOrganizations),
                relatedNames: computeArrayDelta(state.relatedNames, state.updatedRelatedNames)
            };
        },
        
        // Determine if there are any changes in the profile data
        hasChanges: (state) => () => {
            const profileChanges = computeObjectDelta(state.profile, state.updatedProfile);
            const organizationsDelta = computeArrayDelta(state.organizations, state.updatedOrganizations);
            const relatedNamesDelta = computeArrayDelta(state.relatedNames, state.updatedRelatedNames);
            
            return (
                Object.keys(profileChanges).length > 0 ||
                organizationsDelta.added.length > 0 ||
                organizationsDelta.updated.length > 0 ||
                organizationsDelta.deleted.length > 0 ||
                relatedNamesDelta.added.length > 0 ||
                relatedNamesDelta.updated.length > 0 ||
                relatedNamesDelta.deleted.length > 0
            );
        },
        
        // New getter to provide a complete user profile combining profile with organizations and relatedNames
        completeUserProfile: (state) => {
            if (!state.profile) {
                return null;
            }
            
            // Create a deep copy of the profile to avoid reactivity issues
            const completeProfile = cloneDeep(state.profile);
            
            // Add organizations and relatedNames arrays
            completeProfile.organizations = cloneDeep(state.organizations || []);
            completeProfile.relatedNames = cloneDeep(state.relatedNames || []);
            
            return completeProfile;
        }
    },

    actions: {
        // Helper function to deduplicate arrays by ID
        deduplicateArrayById<T>(array: T[]): T[] {
            return thoroughlyDeduplicateArray<T>(array, 'id');
        },

        setState<K extends keyof ProfileState>(payload: { key: K; value: ProfileState[K] }) {
            this.$patch(state => {
                state[payload.key] = payload.value;
            });
        },
        setNestedState(path: string, value: any) {
            const keys = path.split('.');
            let stateRef: any = this.$state;
            keys.forEach((key, index) => {
                if (index === keys.length - 1) {
                    stateRef[key] = value;
                } else {
                    if (!stateRef[key]) {
                        this.$patch((state: any) => {
                            state[key] = {};
                        });
                    }
                    stateRef = stateRef[key];
                }
            });
        },
        // New Firebase binding methods
        async bindFirebaseCollection<T>(
            collectionName: string,
            stateKey: keyof ProfileState,
            sortCriteria?: [string, ('asc' | 'desc')][],
            filterField?: string,
            filterOperator?: WhereFilterOp,
            filterValue?: any
        ): Promise<void> {
            // Check demo mode Firebase operation blocking
            const { useDemo } = await import('@/composables/useDemo');
      const demo = useDemo();
            
            if (demo.isDemoMode.value && !demo.shouldAllowFirebaseOperation.value) {
                console.warn(`[ProfileStore] Firebase operation blocked in demo mode: bindFirebaseCollection(${collectionName})`);
                return;
            }

            const userStore = useUserStore();
            const authUser = userStore.authUser;

            if (!authUser || !authUser.uid) {
                console.error(`No authenticated user. Cannot bind ${collectionName}.`);
                return;
            }

            const boundKey = `${stateKey}`;
            if (this.$state.boundCollections.has(boundKey)) {
                console.log(`Collection ${collectionName} is already bound to ${stateKey}`);
                return;
            }

            return new Promise((resolve, reject) => {
                let firebaseRef: CollectionReference | Query = collection(
                    firestore,
                    'users',
                    authUser.uid,
                    collectionName
                );

                if (filterField && filterValue !== undefined && filterOperator) {
                    firebaseRef = query(firebaseRef, where(filterField, filterOperator, filterValue));
                }

                if (sortCriteria && sortCriteria.length > 0) {
                    // Add each orderBy clause to the query
                    sortCriteria.forEach(([field, direction]) => {
                        firebaseRef = query(firebaseRef, orderBy(field, direction));
                    });
                }

                onSnapshot(firebaseRef, (snapshot) => {
                    let data: T[] = [];
                    snapshot.forEach((doc) => {
                        data.push({ id: doc.id, ...doc.data() } as T);
                    });

                    // Use lodash's orderBy for client-side sorting if needed
                    if (sortCriteria && sortCriteria.length > 0) {
                        const fields = sortCriteria.map(criteria => criteria[0]);
                        const orders = sortCriteria.map(criteria => criteria[1]);
                        data = orderByLodash(data, fields, orders) as T[];
                    }

                    // Deduplicate the data before setting state
                    if (stateKey === 'organizations' || stateKey === 'relatedNames') {
                        data = thoroughlyDeduplicateArray(data, 'id', stateKey as string) as T[];
                    }

                    this.setState({ key: stateKey, value: data as any });
                    this.$state.boundCollections.add(boundKey);
                    console.log(`Successfully bound ${collectionName} to ${stateKey}`);
                    resolve();
                }, (error) => {
                    console.error(`Error binding ${collectionName}:`, error);
                    reject(error);
                });
            });
        },

        // Bind organizations collection from Firebase
        async bindOrganizations() {
            await this.bindFirebaseCollection<ExtendedOrganizationItem[]>(
                'organizations',
                'organizations',
                [['createdAt', 'desc']]
            );
            this.setState({ key: 'updatedOrganizations', value: this.$state.organizations });
        },

        // Bind related names collection from Firebase
        async bindRelatedNames() {
            await this.bindFirebaseCollection<ExtendedRelatedNameItem[]>(
                'related_names',
                'relatedNames',
                [['createdAt', 'desc']]
            );
            this.setState({ key: 'updatedRelatedNames', value: this.$state.relatedNames });
        },

        /**
         * Bind social links with backward compatibility
         * First checks for social_links subcollection (new format from jello-profile)
         * Falls back to social array on user document (legacy format)
         */
        async bindSocialLinks(uid?: string) {
            const userStore = useUserStore();
            const targetUid = uid || userStore.authUser?.uid;

            if (!targetUid) {
                console.log('[bindSocialLinks] No user UID available');
                return;
            }

            const boundKey = 'socialLinks';
            if (this.$state.boundCollections.has(boundKey)) {
                console.log('Social links collection is already bound');
                return;
            }

            try {
                // First try to get from subcollection (new format)
                const socialLinksRef = collection(
                    firestore,
                    'users',
                    targetUid,
                    'social_links'
                );

                const socialLinksQuery = query(socialLinksRef, orderBy('order', 'asc'));

                // Check if subcollection has data
                const snapshot = await getDocs(socialLinksQuery);
                
                if (!snapshot.empty) {
                    // Use subcollection data with real-time listener
                    onSnapshot(socialLinksQuery, (querySnapshot) => {
                        let data: any[] = [];
                        querySnapshot.forEach((doc) => {
                            data.push({ id: doc.id, ...doc.data() });
                        });

                        data = orderByLodash(data, ['order'], ['asc']);
                        data = thoroughlyDeduplicateArray(data, 'id', 'socialLinks');

                        this.setState({ key: 'socialLinks', value: data });
                        this.setState({ key: 'updatedSocialLinks', value: [...data] });
                        this.$state.boundCollections.add(boundKey);
                        this.setState({ key: 'socialLinksMigrated', value: true });
                        
                        console.log('[bindSocialLinks] Using subcollection format:', data.length);
                    }, (error) => {
                        console.error('[bindSocialLinks] Snapshot error:', error);
                    });
                } else {
                    // Fallback to legacy social array on user document
                    console.log('[bindSocialLinks] No subcollection data, using legacy social array');
                    
                    const user = userStore.user;
                    if (user && Array.isArray(user.social) && user.social.length > 0) {
                        // Transform legacy format to new format
                        const legacyData = user.social.map((item: any, index: number) => ({
                            id: item.id || `legacy-${index}`,
                            platform: item.id || item.name?.toLowerCase() || '',
                            name: item.name || '',
                            icon: item.icon || '',
                            url: item.url || '',
                            handle: item.handle || '',
                            showHandle: item.showHandle ?? true,
                            order: index
                        }));
                        
                        this.setState({ key: 'socialLinks', value: legacyData });
                        this.setState({ key: 'updatedSocialLinks', value: [...legacyData] });
                        this.$state.boundCollections.add(boundKey);
                        this.setState({ key: 'socialLinksMigrated', value: false });
                        
                        console.log('[bindSocialLinks] Using legacy format:', legacyData.length);
                    } else {
                        // No social links at all
                        this.setState({ key: 'socialLinks', value: [] });
                        this.setState({ key: 'updatedSocialLinks', value: [] });
                        this.$state.boundCollections.add(boundKey);
                    }
                }
            } catch (error) {
                console.error('[bindSocialLinks] Error:', error);
            }
        },

        /**
         * Get combined social links - prefers subcollection, falls back to legacy array
         * Use this for displaying social links in views
         */
        getCombinedSocialLinks(user?: any): any[] {
            // First check if we have subcollection data
            if (this.$state.socialLinks && this.$state.socialLinks.length > 0) {
                return this.$state.socialLinks;
            }
            
            // Fall back to legacy format from user document
            const targetUser = user || this.$state.profile;
            if (targetUser && Array.isArray(targetUser.social)) {
                return targetUser.social.map((item: any, index: number) => ({
                    id: item.id || `legacy-${index}`,
                    platform: item.id || item.name?.toLowerCase() || '',
                    name: item.name || '',
                    icon: item.icon || '',
                    url: item.url || '',
                    handle: item.handle || '',
                    showHandle: item.showHandle ?? true,
                    order: index
                }));
            }
            
            return [];
        },

        // Helper method to synchronize profile array data
        syncProfileArrayData<K extends 'organizations' | 'relatedNames' | 'socialLinks'>(key: K, value: any) {
            if (this.$state.profile) {
                // Ensure we're deduplicating before syncing
                this.$state.profile[key] = thoroughlyDeduplicateArray(value, 'id', `profile.${key}`);
            }
        },
        updateArrayItem<K extends ArrayKeys<ProfileState>>(
            payload: { key: K; index: number; value: ArrayElement<ProfileState[K]> }
        ) {
            this.$patch((state) => {
                const array = state[payload.key] as Array<ArrayElement<ProfileState[K]>>;

                if (Array.isArray(array) && payload.index >= 0 && payload.index < array.length) {
                    array.splice(payload.index, 1, payload.value);

                    // Deduplicate for organizations and relatedNames arrays
                    if ((payload.key === 'organizations' || payload.key === 'relatedNames')) {
                        const uniqueItems = thoroughlyDeduplicateArray(array as any[], 'id', payload.key as string);

                        // Safely assign the deduplicated array back
                        while (array.length > 0) {
                            array.pop();
                        }
                        uniqueItems.forEach(item => array.push(item as any));
                    }

                    // Synchronize the profile object as well
                    if ((payload.key === 'organizations' || payload.key === 'relatedNames') && state.profile) {
                        if (Array.isArray(state.profile[payload.key])) {
                            state.profile[payload.key].splice(payload.index, 1, payload.value);

                            // Deduplicate the profile array
                            const uniqueItemsInProfile = thoroughlyDeduplicateArray(
                                state.profile[payload.key] as any[],
                                'id',
                                `profile.${payload.key}`
                            );

                            state.profile[payload.key] = uniqueItemsInProfile;
                        } else {
                            state.profile[payload.key] = thoroughlyDeduplicateArray(array as any[], 'id', `profile.${payload.key}`);
                        }
                    }
                } else {
                    console.error(`Invalid array or index for state key '${payload.key}'.`);
                }
            });
        },

        addArrayItem<K extends ArrayKeys<ProfileState>>(
            payload: { key: K; value: ArrayElement<ProfileState[K]> }
        ) {
            this.$patch((state) => {
                const array = state[payload.key] as Array<ArrayElement<ProfileState[K]>>;

                if (Array.isArray(array)) {
                    // For organizations and relatedNames, check for duplicates by ID
                    if ((payload.key === 'organizations' || payload.key === 'relatedNames') &&
                        (payload.value as any).id) {
                        const existingIndex = array.findIndex((item: any) =>
                            item.id === (payload.value as any).id);

                        if (existingIndex !== -1) {
                            // Update instead of adding if the item already exists
                            array.splice(existingIndex, 1, payload.value);
                        } else {
                            // Add the new item if it doesn't exist
                            array.push(payload.value);
                        }

                        // Always deduplicate after modifying
                        const uniqueItems = thoroughlyDeduplicateArray(array as any[], 'id', payload.key as string);

                        // Replace the array with deduplicated items
                        while (array.length > 0) {
                            array.pop();
                        }
                        uniqueItems.forEach(item => array.push(item as any));
                    } else {
                        // For other array types, just push
                        array.push(payload.value);
                    }

                    // Synchronize the profile object as well
                    if ((payload.key === 'organizations' || payload.key === 'relatedNames') && state.profile) {
                        if (!Array.isArray(state.profile[payload.key])) {
                            state.profile[payload.key] = [];
                        }

                        // Check if the item already exists in the profile array
                        if ((payload.value as any).id && Array.isArray(state.profile[payload.key])) {
                            const existingProfileIndex = state.profile[payload.key].findIndex((item: any) =>
                                item.id === (payload.value as any).id);

                            if (existingProfileIndex !== -1) {
                                // Update instead of adding if the item already exists
                                state.profile[payload.key].splice(existingProfileIndex, 1, payload.value);
                            } else {
                                // Add the new item if it doesn't exist
                                state.profile[payload.key].push(payload.value);
                            }

                            // Deduplicate the profile array
                            state.profile[payload.key] = thoroughlyDeduplicateArray(
                                state.profile[payload.key] as any[],
                                'id',
                                `profile.${payload.key}`
                            );
                        } else if (Array.isArray(state.profile[payload.key])) {
                            state.profile[payload.key].push(payload.value);

                            // Deduplicate the profile array
                            state.profile[payload.key] = thoroughlyDeduplicateArray(
                                state.profile[payload.key] as any[],
                                'id',
                                `profile.${payload.key}`
                            );
                        }
                    }
                } else {
                    console.error(`State key '${payload.key}' is not an array.`);
                }
            });
        },

        removeArrayItem<K extends ArrayKeys<ProfileState>>(
            payload: { key: K; index: number }
        ) {
            this.$patch((state) => {
                const array = state[payload.key] as Array<ArrayElement<ProfileState[K]>>;

                if (Array.isArray(array) && payload.index >= 0 && payload.index < array.length) {
                    array.splice(payload.index, 1);

                    // Synchronize the profile object as well
                    if ((payload.key === 'organizations' || payload.key === 'relatedNames') &&
                        state.profile && Array.isArray(state.profile[payload.key])) {
                        state.profile[payload.key].splice(payload.index, 1);

                        // Ensure arrays are deduplicated
                        state.profile[payload.key] = thoroughlyDeduplicateArray(
                            state.profile[payload.key] as any[],
                            'id',
                            `profile.${payload.key}`
                        );
                    }
                } else {
                    console.error(`Invalid array or index for state key '${payload.key}'.`);
                }
            });
        },
        reset() {
            // Reset the entire state using Pinia's $reset method
            this.$reset();
        },

        /**
         * Updates the complete user profile object while preserving bound organizations and related names.
         * This serves as the central method for updating the user profile in a holistic way.
         * @param userObject The complete user object with updates
         */
        updateUserProfile(userObject: any) {
            if (!userObject) return;

            this.$patch((state) => {
                // Preserve the migration status or use the one from userObject if provided
                const organizationsMigrated = 'organizationsMigrated' in userObject
                    ? !!userObject.organizationsMigrated
                    : state.organizationsMigrated;
                
                // Create a new profile object that combines the updated user info with existing data
                const updatedProfile = {
                    ...state.profile,
                    ...userObject,
                    // Use the organizations and relatedNames from the user object if provided
                    // otherwise preserve the existing ones from the store
                    organizations: Array.isArray(userObject.organizations) && userObject.organizations.length > 0
                        ? userObject.organizations
                        : state.organizations,
                    relatedNames: Array.isArray(userObject.relatedNames) && userObject.relatedNames.length > 0
                        ? userObject.relatedNames
                        : state.relatedNames,
                    // Make sure the migration flag is preserved
                    organizationsMigrated
                };
                
                // If collegiate data exists in the profile but organizationsMigrated is true,
                // remove the collegiate field
                if (updatedProfile.organizationsMigrated && 'collegiate' in updatedProfile) {
                    delete updatedProfile.collegiate;
                }

                // Update the profile in the store
                state.profile = updatedProfile;
                
                // Also update the state's organizationsMigrated flag
                state.organizationsMigrated = organizationsMigrated;

                console.log('Profile store updated with complete user object');
            });
        },

        // New action to update the updatedProfile state
        updateProfileField(field: string, value: any) {
            if (!this.updatedProfile) {
                this.updatedProfile = cloneDeep(this.profile) || {};
            }
            
            if (this.updatedProfile) {
                this.$patch(state => {
                    if (state.updatedProfile) {
                        (state.updatedProfile as any)[field] = value;
                    }
                });
            }
        },
        
        // New action to discard changes and reset to original values
        discardChanges() {
            this.$patch(state => {
                state.updatedProfile = cloneDeep(state.profile);
                state.updatedOrganizations = [...state.organizations];
                state.updatedRelatedNames = [...state.relatedNames];
            });
        },
        
        // New action to apply deltas and save changes to original values
        applyChanges() {
            this.$patch(state => {
                state.profile = cloneDeep(state.updatedProfile);
                state.organizations = [...state.updatedOrganizations];
                state.relatedNames = [...state.updatedRelatedNames];
            });
        },

        // New action to mark profile as fully initialized
        setProfileInitialized() {
            this.$patch(state => {
                state.profileInitialized = true;
            });
        },

        // Helper to check if profile is properly transformed for editing
        isProfileTransformed(): boolean {
            if (!this.profile) return false;
            
            // Check for key transformation indicators
            const hasBaseInfoStructure = this.profile.baseInfo && 
                typeof this.profile.baseInfo === 'object';
            const hasNameStructure = this.profile.name && 
                typeof this.profile.name === 'object';
            
            return hasBaseInfoStructure && hasNameStructure;
        },

        // Helper to validate profile integrity
        validateProfileIntegrity(userUid: string): boolean {
            return this.profile?.uid === userUid && 
                   this.isProfileTransformed() && 
                   this.profileInitialized;
        },

        // New action to set the profile as ready for editing
        setReadyForEditing(isReady: boolean) {
            this.$patch(state => {
                state.isReadyForEditing = isReady;
            });
        },

        // New action to reset and update with import user data
        resetAndUpdateWithImportUser(userObject: any) {
            // First reset the store to default state
            this.$reset();
            
            // Then update with the imported user data
            if (userObject) {
                // Set the profile with the user object
                this.$patch(state => {
                    state.profile = { ...userObject };
                    state.updatedProfile = { ...userObject };
                    
                    // Handle organizations if present
                    if (Array.isArray(userObject.organizations)) {
                        state.organizations = thoroughlyDeduplicateArray(userObject.organizations, 'id', 'organizations');
                        state.updatedOrganizations = [...state.organizations];
                    }
                    
                    // Handle related names if present
                    if (Array.isArray(userObject.relatedNames)) {
                        state.relatedNames = thoroughlyDeduplicateArray(userObject.relatedNames, 'id', 'relatedNames');
                        state.updatedRelatedNames = [...state.relatedNames];
                    }
                    
                    // Mark the profile as initialized
                    state.profileInitialized = true;
                });
                
                console.log('Profile store reset and updated with import user data');
            } else {
                console.error('Cannot update profile with null/undefined user object');
            }
        },

        // Method to check and set the migration status
        checkAndUpdateMigrationStatus() {
            this.$patch(state => {
                // If we have a profile and it has the organizationsMigrated flag, use that value
                if (state.profile && 'organizationsMigrated' in state.profile) {
                    state.organizationsMigrated = !!state.profile.organizationsMigrated;
                    return;
                }
                
                // If we have a profile with collegiate data, we're not migrated yet
                if (state.profile && 'collegiate' in state.profile && 
                    Array.isArray(state.profile.collegiate) && 
                    state.profile.collegiate.length > 0) {
                    state.organizationsMigrated = false;
                    return;
                }
                
                // If we have organizations but no collegiate data, we can consider it migrated
                if (state.organizations && state.organizations.length > 0 && 
                    (!state.profile || !('collegiate' in state.profile))) {
                    state.organizationsMigrated = true;
                    return;
                }
                
                // Default - not migrated
                state.organizationsMigrated = false;
            });
        },

        // Method to initialize watchers for keeping profile and other collections in sync
        initializeWatchers() {
            if (this.watchersInitialized) {
                console.log('Watchers already initialized for profile store');
                return;
            }

            // Set up watchers to sync organizations with profile
            watch(() => this.organizations, (newOrganizations) => {
                if (this.profile) {
                    this.syncProfileArrayData('organizations', newOrganizations);
                }
            }, { deep: true });

            // Set up watchers to sync related names with profile
            watch(() => this.relatedNames, (newRelatedNames) => {
                if (this.profile) {
                    this.syncProfileArrayData('relatedNames', newRelatedNames);
                }
            }, { deep: true });

            this.$patch(state => {
                state.watchersInitialized = true;
            });

            console.log('Profile store watchers initialized successfully');
        }
    },
});